import { Card, CardContent, CardHeader, Icon, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import ScrollContainer from 'react-indiana-drag-scroll'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import useCurrentUser from '../../hooks/useCurrentUser'
import useFacilityPaymentStatus from '../../hooks/useFacilityPaymentStatus'
import NoFacilityCardWarning from '../Common/NoFacilityCardWarning'

import {
	getIconAndTagByFoundingAndMembership,
	VISIT_TYPE_DISPLAY_NAMES,
	ADDON_ONLY,
	GROOMING,
	BOARDING
} from '../../utils'

const useStyles = makeStyles(() => ({
	row: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between'
	},
	section: {
		flex: '218px 0 0'
	},
	sectionSpecificLeft: {
		flex: '200px 0 0'
	},
	sectionSpecificRight: {
		flex: '225px 0 0'
	},
	sectionHeading: {
		marginBottom: '24px'
	},
	cardActions: {
		justifyContent: 'space-between'
	},
	deleteButton: {
		color: '#D32F2F',
		borderColor: '#D32F2F',
		'&:hover': {
			color: '#FFFFFF',
			backgroundColor: '#D32F2F'
		}
	},
	acct: {
		color: '#4D4C4F',
		fontSize: 16,
		fontWeight: '600'
	},
	membershipWrapper: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		marginTop: 8
	},
	membershipIcon: {
		color: '#6B4AD6',
		'&::before': {
			fontSize: '14px',
			lineHeight: '14px'
		}
	},
	membershipType: {
		fontFamily: 'Nunito',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '19px',
		color: '#777679',
		textTransform: 'capitalize'
	},
	depositBox: {
		padding: 16,
		paddingTop: 24,
		paddingBottom: 24
	},
	depositWarning: {
		backgroundColor: 'rgba(235, 76, 76, 0.1)',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		borderRadius: 6,
		padding: 16
	},
	depositText: {
		color: '#D02C2C',
		fontSize: 16,
		fontWeight: '400'
	}
}))

function useQuery() {
	return new URLSearchParams(useLocation().search)
}

const VisitDetailsCard = (props) => {
	const classes = useStyles()
	const facility = useSelector((state) => state.specific.facilities.selected)
	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const { visit, date } = props
	const [iconName, iconColor, tag] = getIconAndTagByFoundingAndMembership(visit.household)
	const [visitFacility, setVisitFacility] = useState(null)

	const isAddOnGroomingVisitOnly = visit?.type === ADDON_ONLY && visit?.subtype === GROOMING

	// Cards are saved per facility, so check the facility this visit is happening at
	// rather than whether the household has a card anywhere.
	const facilityCardStatus = useFacilityPaymentStatus(
		visit?.household_id || visit?.household?._id,
		visit?.facility_id
	)

	// const { hasRelatedPetCareVisit } = props

	useEffect(() => {
		if (visit && visit.facility_id && facilities && facilities.length) {
			setVisitFacility(facilities.find((f) => f._id === visit.facility_id))
		}
	}, [])

	const renderAccounts = () => {
		if (!visit.household || !visit.household.accounts) return null
		let ourAccts = visit.household.accounts.sort((a, b) => {
			if (a.primary) return 1
			return 0
		})

		ourAccts = ourAccts.map((acct, i) => {
			return (
				<div
					className={clsx([
						classes.section,
						{
							[classes.sectionSpecificLeft]: i === 2,
							[classes.sectionSpecificRight]: i === 3
						}
					])}
					style={{ overflow: 'hidden', paddingRight: '20px' }}>
					<Typography variant="overline">{acct.primary ? 'Primary' : 'Alt'} Contact</Typography>
					{renderAccount(acct)}
				</div>
			)
		})

		if (visit.preferred_contact) {
			const prefContact = (
				<div className={clsx([classes.section])} style={{ overflow: 'hidden', paddingRight: '20px' }}>
					<Typography variant="overline">Preferred Contact</Typography>
					{renderAccount(visit.preferred_contact)}
				</div>
			)
			ourAccts = [prefContact, ...ourAccts]
		}

		return ourAccts
	}

	const renderAccount = (acct) => {
		return (
			<div>
				<Typography className={classes.acct}>{`${acct.first_name} ${acct.last_name}`}</Typography>
				<Typography className={classes.acct}>
					{acct.phone_number ? acct.phone_number : 'No Phone Number'}
				</Typography>
				<Typography
					className={classes.acct}
					style={{
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						overflow: 'hidden'
					}}>
					{acct.email ? acct.email : 'No email'}
				</Typography>
			</div>
		)
	}

	const getPickUpType = () => {
		if (!facility) return null

		if (visit?.type === GROOMING) {
			return visit.pickup_time_string || 'TBD'
		}

		const isExtendedVisit = visit.extended_pickup

		if (visit?.type === BOARDING) {
			if (!visit.last_day_daycare) return 'AM Pick-up'
			if (visit.last_day_daycare && !isExtendedVisit) return 'Standard'
			if (visit.last_day_daycare && isExtendedVisit) return 'Late'
		}

		return isExtendedVisit ? 'Late Pick-up' : 'Standard'
	}

	const getDropOffType = () => {
		if (!facility) return null

		if (isAddOnGroomingVisitOnly) {
			return 'Walk-In'
		}

		if (visit?.type === GROOMING) {
			return visit.dropoff_time_string || 'TBD'
		}

		const isEarlyDropOff = facility.early_dropoff_enabled && visit.early_dropoff

		if (visit?.type === BOARDING) {
			if (!visit.first_day_daycare) return 'Overnight'
			if (visit.first_day_daycare && !isEarlyDropOff) return 'Standard'
			if (visit.first_day_daycare && isEarlyDropOff) return 'Early'
		}

		return isEarlyDropOff ? 'Early Drop Off' : 'Standard'
	}

	return (
		<Card style={{ marginTop: '24px', marginBottom: '24px' }}>
			{visit.deposit_paid && <DepositWarning />}
			<NoFacilityCardWarning
				facilityName={facilityCardStatus.facilityName || visitFacility?.name}
				hasPaymentMethod={facilityCardStatus.hasPaymentMethod}
				loading={facilityCardStatus.loading}
				style={{ margin: '16px 16px 0' }}
			/>
			<CardHeader title="Appointment Details" />
			<CardContent>
				<div>
					<div className={classes.row}>
						<div className={classes.section}>
							<div className={classes.sectionHeading}>
								<Typography variant="overline">Appointment Type</Typography>
								<Typography variant="h5" style={{ textTransform: 'capitalize', fontWeight: 800 }}>
									{visit?.type
										? VISIT_TYPE_DISPLAY_NAMES[visit.type] || visit.type.replace('_', ' ')
										: 'UNKNOWN'}
								</Typography>
								{visitFacility && visitFacility.name && (
									<Typography variant="body1">{visitFacility.name}</Typography>
								)}
							</div>
						</div>
						<div className={classes.section}>
							<div className={classes.sectionHeading}>
								<Typography variant="overline">Date</Typography>
								<Typography variant="h5" style={{ textTransform: 'capitalize', fontWeight: 800 }}>
									{date}
									{visit.time_block ? `, ${visit.time_block}` : ''}
								</Typography>
							</div>
						</div>
						<div className={clsx([classes.section, classes.sectionSpecificLeft])}>
							<div className={classes.sectionHeading}>
								<Typography variant="overline">Drop-off</Typography>
								<Typography variant="h5" style={{ textTransform: 'capitalize', fontWeight: 800 }}>
									{getDropOffType()}
								</Typography>
							</div>
						</div>
						<div className={clsx([classes.section, classes.sectionSpecificRight])}>
							{!isAddOnGroomingVisitOnly ? (
								<div className={classes.sectionHeading}>
									<Typography variant="overline">Pick-up</Typography>
									<Typography
										variant="h5"
										style={{ textTransform: 'capitalize', fontWeight: 800 }}>
										{getPickUpType()}
									</Typography>
								</div>
							) : null}
						</div>
					</div>
					<ScrollContainer
						className={classes.row}
						style={{ justifyContent: 'start', overflow: 'scroll' }}>
						{renderAccounts()}
					</ScrollContainer>
					{!!iconName || !!tag ? (
						<div className={classes.membershipWrapper}>
							{!!iconName && (
								<Icon
									className={`fa fa-${iconName}`}
									style={{
										color: iconColor,
										fontWeight: '900',
										fontSize: '14px',
										lineHeight: '16px',
										width: 20
									}}
								/>
							)}
							<Typography className={classes.membershipType}>{tag}</Typography>
						</div>
					) : null}
				</div>
			</CardContent>
		</Card>
	)
}

export default VisitDetailsCard

const DepositWarning = () => {
	const classes = useStyles()
	const { isBackOfficeUser, isPcaSupervisorUser } = useCurrentUser()

	return !isBackOfficeUser && !isPcaSupervisorUser ? (
		<div className={classes.depositBox}>
			<div className={classes.depositWarning}>
				<i className="fa fa-exclamation-circle" style={{ color: '#D02C2C', marginRight: 12 }} />
				<Typography className={classes.depositText}>
					Shortening holiday appointments may charge a fee, please ask a PCA supervisor to make this
					change.
				</Typography>
			</div>
		</div>
	) : null
}
